import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import type {ColumnDef} from "@tanstack/react-table";
import type {
    TransactionalMailTemplate
} from "crqlar-react-common/dist/models/newsletter/transactionalMailTemplate.model";
import {
    TransactionalMailTemplateService
} from "crqlar-react-common/dist/services/newsletter/transactionalMailTemplate.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import CreateTransactionalMailTemplateDialog from "./CreateTransactionalMailTemplateDialog";

const TABLE_ID = 'system-transactional-mail-templates';
const TEMPLATE_COPY_EXCLUDED_FIELDS: Array<keyof TransactionalMailTemplate> = [
    'id',
    'insertDate',
    'updateDate',
    'organizationId',
    'chainId',
    'groupId',
    'idV2',
    'cidV2',
    'archived',
    'tags'
];

const TransactionalMailTemplateList = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const readOnlyUser = useReadOnly();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [templateToEdit, setTemplateToEdit] = useState<TransactionalMailTemplate>();
    const [cloningId, setCloningId] = useState<string | null>(null);

    const refreshTable = useCallback(
        () => queryClient.invalidateQueries(['server-table', TABLE_ID]),
        [queryClient]
    );
    const handleCreate = useCallback(() => {
        setTemplateToEdit(undefined);
        setDialogOpen(true);
    }, []);
    const handleEdit = useCallback((template: TransactionalMailTemplate) => {
        setTemplateToEdit(template);
        setDialogOpen(true);
    }, []);
    const handleDelete = useCallback(async (id: string) => {
        await TransactionalMailTemplateService.delete(id);
        await refreshTable();
    }, [refreshTable]);
    const handleClone = useCallback(async (template: TransactionalMailTemplate) => {
        if (!template.id) return;

        setCloningId(template.id);
        try {
            const copyableTemplate = {...template};
            TEMPLATE_COPY_EXCLUDED_FIELDS.forEach(field => delete copyableTemplate[field]);
            await TransactionalMailTemplateService.create({
                ...copyableTemplate,
                name: t('Copy of {{name}}', {name: template.name})
            });
            await refreshTable();
        } finally {
            setCloningId(null);
        }
    }, [refreshTable, t]);
    const columns = useMemo<ColumnDef<TransactionalMailTemplate>[]>(() => [
        {
            id: 'name',
            header: t('Template Name'),
            accessorFn: template => template.name,
            cell: ({row}) => <Box>{row.original.name || '-'}</Box>,
            size: 220,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'fromName',
            header: t('From Name'),
            accessorFn: template => template.fromName,
            cell: ({row}) => <Box>{row.original.fromName || '-'}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'fromEmail',
            header: t('Sender Email Address'),
            accessorFn: template => template.fromEmail,
            cell: ({row}) => <Box>{row.original.fromEmail || '-'}</Box>,
            size: 240,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'subject',
            header: t('Subject Line'),
            accessorFn: template => template.subject,
            cell: ({row}) => <Box>{row.original.subject || '-'}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: template => template.id,
            cell: ({row}) => <Box>{row.original.id || ''}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'actions',
            header: t('Actions'),
            cell: ({row}) => (
                <Box data-no-row-click sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Tooltip title={t('Edit template')}>
                        <IconButton
                            disabled={readOnlyUser}
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(row.original)}
                        >
                            <EditOutlinedIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('Clone template')}>
                        <span>
                            <IconButton
                                size="small"
                                color="primary"
                                disabled={cloningId === row.original.id || readOnlyUser}
                                onClick={() => handleClone(row.original)}
                            >
                                {cloningId === row.original.id
                                    ? <CircularProgress size={16}/>
                                    : <ContentCopyIcon fontSize="small"/>}
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={t('Delete')}>
                        <IconButton
                            disabled={readOnlyUser}
                            size="small"
                            color="error"
                            onClick={() => row.original.id && handleDelete(row.original.id)}
                        >
                            <DeleteOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            size: 160,
            enableResizing: false,
            meta: {defaultActive: true}
        }
    ], [cloningId, handleClone, handleDelete, handleEdit, readOnlyUser, t]);
    const controller = useServerTable({
        tableId: TABLE_ID,
        columns,
        getRowId: template => template.id!,
        onRowClick: handleEdit,
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, sorting}) => {
            const page = await TransactionalMailTemplateService.list({
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', height: '70vh', minHeight: 0}}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>

                        <ServerTableCreate
                            label={t('Create Template')}
                            disabled={readOnlyUser}
                            onClick={handleCreate}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            <CreateTransactionalMailTemplateDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={refreshTable}
                templateToEdit={templateToEdit}
            />
        </Box>
    );
};

export default TransactionalMailTemplateList;
